import { useCallback, useEffect, useRef } from 'react'
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react'
import { Virtual } from 'swiper/modules'
import './desktop-slider.css'
import { StoryPeerType } from '@weblite/ts-action/story'
// Import Swiper styles
import {
  PeerStoryViewer,
  StoryViewerActionsApi,
} from '$comp/_app/components/story/peer-story-viewer/peer-story-viewer'
import {
  useStoryRibbons,
  useSelectedRibbonStory,
} from '$logic/story/react-hooks'
import { DesktopModeActionButtons } from './desktop-mode-action-buttons'

export function DesktopSlider({
  peerId,
  peerType,
  isOpen,
  onSwipeNext,
  onSwipePrevious,
  onClose,
  onCreateStory,
}: {
  peerId: string
  peerType: StoryPeerType
  isOpen: boolean
  onSwipeNext: () => void
  onSwipePrevious: () => void
  onClose: () => Promise<void>
  onCreateStory: (peerId: string, peerType: StoryPeerType) => void
}) {
  const { allRibbons } = useStoryRibbons()

  const enteringSwipeHappenedRef = useRef(false)

  const isSliderMovingRef = useRef(false)
  const selectedRibbonStoryIndex = useSelectedRibbonStory(peerId, peerType)
  useEffect(() => {
    if (selectedRibbonStoryIndex === 0) enteringSwipeHappenedRef.current = true
  }, [selectedRibbonStoryIndex])

  const handleSliderMoveStart = useCallback(() => {
    isSliderMovingRef.current = true
  }, [])

  const handleSliderMoveEnd = useCallback(() => {
    isSliderMovingRef.current = false
  }, [])
  const swiperRef = useRef<SwiperClass>()

  const handleInitSwiper = useCallback((swiper: SwiperClass) => {
    swiperRef.current = swiper
  }, [])

  const actionRef = useRef<StoryViewerActionsApi>(null)

  const handleGoToNextSlide = useCallback(() => {
    const swiper = swiperRef.current
    if (!swiper) return
    if (swiper.isEnd) onClose()
    else swiper.slideNext()
  }, [])

  const handleGoToPrevSlide = useCallback(() => {
    const swiper = swiperRef.current
    if (!swiper) return
    if (swiper.isBeginning) onClose()
    else swiper.slidePrev()
  }, [])

  const handleSwipeNext = useCallback(() => {
    if (enteringSwipeHappenedRef.current) {
      onSwipeNext()
    } else {
      enteringSwipeHappenedRef.current = true
    }
  }, [onSwipeNext])

  useEffect(() => {
    if (!allRibbons.length) {
      onClose()
    }
  }, [allRibbons])

  return (
    <>
      <Swiper
        className="desktop-story-slider"
        modules={[Virtual]}
        style={{ width: '100%', height: '100%', padding: '48px 30px' }}
        spaceBetween={40}
        centeredSlides
        virtual
        initialSlide={selectedRibbonStoryIndex}
        dir="ltr"
        touchStartPreventDefault={false}
        onTransitionStart={handleSliderMoveEnd}
        slidesPerView={3}
        onSliderMove={handleSliderMoveStart}
        allowTouchMove={false}
        onSwiper={handleInitSwiper}
        onSlideNextTransitionEnd={handleSwipeNext}
        onSlidePrevTransitionEnd={onSwipePrevious}
      >
        {allRibbons.map((storyRibbon, index) => (
          <SwiperSlide
            dir="rtl"
            className="swiper-slide-item"
            virtualIndex={index}
            key={storyRibbon.peerId + storyRibbon.peerType}
            onClick={() => swiperRef.current?.slideTo(index)}
          >
            {({ isActive }) => (
              <PeerStoryViewer
                onCreateStory={onCreateStory}
                onClose={onClose}
                isActive={isActive}
                storyRibbon={storyRibbon}
                isSliderMovingRef={isSliderMovingRef}
                desktopMode
                goToNextSlide={handleGoToNextSlide}
                goToPrevSlide={handleGoToPrevSlide}
                ref={isActive ? actionRef : null}
                isModalOpen={isOpen}
                ribbonsCount={allRibbons.length}
              />
            )}
          </SwiperSlide>
        ))}
      </Swiper>
      <DesktopModeActionButtons actionRef={actionRef} />
    </>
  )
}
